import { sameNumber } from "@/lib/manual-call";

/**
 * Who a call is with, read off the caller profile the switch sends.
 *
 * `verto.display` hands the phone the *whole* profile — both ends of the call — and which end is
 * "the other party" is settled by direction, never by which field happens to be populated. We
 * are the caller on a call we placed and the callee on one we took, so the far end is always the
 * opposite one:
 *
 *     outbound → callee_id_*          inbound → caller_id_*
 *
 * Reading the wrong end is not a subtle error. The display update for an outbound PSTN call
 * carries `caller_id_name: "Adnan", caller_id_number: "1008"` — the rep's own extension — so a
 * phone that trusts `caller_id_*` on outbound puts the rep's own name in their own call widget
 * and never shows the number they dialled.
 *
 * `display_direction` is no help in telling the two apart, and is deliberately not consulted
 * here: it reports the FreeSWITCH channel's direction, and a leg dialled from the browser
 * arrives at the switch as an *inbound* channel, so it reads "inbound" on every outbound call.
 * The call's own direction is the thing that is true.
 *
 * This is the same split the platform makes server-side when it turns a channel event into a
 * peer — `event-gateway/src/translate.py` — down to dropping the same placeholder name.
 *
 * Pure, and kept out of the context, so the packets that caused trouble can be pinned in a test
 * instead of re-derived from a live switch.
 */

/** The subset of a call snapshot this needs. Keeps it testable without the vendored client. */
export interface PartyInfo {
  direction: "inbound" | "outbound";
  /** What the rep dialled. All we have on an outbound leg until the first display update. */
  destinationNumber?: string;
  callerIdName?: string;
  callerIdNumber?: string;
  display?: {
    name?: string;
    number?: string;
    callerName?: string;
    callerNumber?: string;
    calleeName?: string;
    calleeNumber?: string;
    direction?: string;
  };
}

/**
 * FreeSWITCH's stand-in callee-id name for a leg dialled without one. It names nobody — the
 * platform strips it out of CDRs and presence for the same reason (`event-gateway/src/raw.py`).
 */
const PLACEHOLDER_NAME = "Outbound Call";

/** The far end's number, or `""` while the switch has not said who that is yet. */
export function remoteNumber(info: PartyInfo): string {
  const d = info.display ?? {};
  return info.direction === "outbound"
    ? d.calleeNumber || d.number || info.destinationNumber || ""
    : d.callerNumber || d.number || info.callerIdNumber || "";
}

/**
 * The far end's name, or null when there is nothing worth printing above the number.
 *
 * Two non-names are dropped. FreeSWITCH's "Outbound Call" placeholder, which is what a PSTN leg
 * comes back with and is worse than blank because it reads as a real display name. And the
 * number repeated as the name, which an unconfigured UA sends — putting "+1 706 702 8378" on
 * both lines of a two-line row.
 *
 * `caller_id_name` is never consulted on an outbound call: it is the rep's own name going out,
 * and the vendored client overwrites it with the display name coming back.
 */
export function remoteName(info: PartyInfo, number: string): string | null {
  const d = info.display ?? {};
  const raw =
    info.direction === "outbound"
      ? d.calleeName || d.name
      : d.callerName || d.name || info.callerIdName;
  const name = raw?.trim();
  return !name || name === PLACEHOLDER_NAME || name === number ? null : name;
}

/**
 * Whether a call is an internal transfer: one ringing in from GoHighLevel's own line, where a rep
 * at GHL is sending a client on to us (`GET /phone/internal-transfer-numbers`, the list Slack's
 * call messages use). The caller id is GHL's, so it names nobody and calling it back reaches GHL,
 * not the client, and the rep has to know that before they pick up.
 *
 * Only a call coming in: a rep who dials one of those numbers is calling GHL, not taking a
 * transfer. Numbers compare as `sameNumber` does, on the last ten digits.
 */
export function isInternalTransfer(
  call: { direction: "inbound" | "outbound"; remote: string },
  numbers: readonly string[],
): boolean {
  return call.direction === "inbound" && numbers.some((n) => sameNumber(call.remote, n));
}

/**
 * `calls` with `internalTransfer` set on each one that is (`isInternalTransfer`). The same array
 * when none is, so a phone with no list configured renders exactly as it did.
 */
export function markInternalTransfers<T extends { direction: "inbound" | "outbound"; remote: string }>(
  calls: T[],
  numbers: readonly string[] | undefined,
): (T & { internalTransfer?: boolean })[] {
  if (!numbers?.length || !calls.some((c) => isInternalTransfer(c, numbers))) return calls;
  return calls.map((c) => (isInternalTransfer(c, numbers) ? { ...c, internalTransfer: true } : c));
}
